"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var widgets_1 = require("@tableau/widgets");
var React = require("react");
var ReactDOM = require("react-dom");
var ReactResizeDetector = require("react-resize-detector");
var TypeStyle = require("typestyle");
var Body_1 = require("./Body");
var DataGridColors_1 = require("./DataGridColors");
var DefaultScrollComponent_1 = require("./DefaultScrollComponent");
var HeaderRow_1 = require("./HeaderRow");
var Paging_1 = require("./Paging");
var RowHeaderCell_1 = require("./RowHeaderCell");
var Utils_1 = require("./Utils");
var ConfigDefaults = /** @class */ (function () {
    function ConfigDefaults() {
    }
    ConfigDefaults.rowOuterHeight = 38;
    ConfigDefaults.sectionRows = 20;
    ConfigDefaults.colsPerChunk = 10;
    ConfigDefaults.maxInitialColumnWidth = 200;
    ConfigDefaults.minimumColumnWidth = 39;
    ConfigDefaults.rememberLastScrollPos = false;
    return ConfigDefaults;
}());
exports.ConfigDefaults = ConfigDefaults;
/** Whether column headers are shown, i.e. as the first row of the grid. */
exports.ColumnHeadersContext = React.createContext(true);
exports.ColumnHeadersContext.displayName = 'DataGridColumnHeadersContext';
var DataGrid = /** @class */ (function (_super) {
    tslib_1.__extends(DataGrid, _super);
    function DataGrid() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.mergedMeasuredColumnHeaderWidths = undefined;
        _this.mergedMeasuredColumnBodyWidths = undefined;
        _this.resizing = false;
        _this.body = null;
        _this.config = {};
        _this.colsPerChunk = 0;
        _this.sectionRows = 0;
        _this.rowOuterHeight = 0;
        _this.minimumColumnWidth = 0;
        // TFSID 1082053: We need to compare the previous render's value against the current value to see if column widths need adjusting.
        _this.scrollbarWasNeeded = false;
        _this.scrollTop = 0;
        // Focus Management
        /* 'active' here means "where focus was most recently observed" --  i.e. even if the user has
           focused outside the DataGrid, we want to keep track of the previous row/col */
        _this.activeRowIndex = 0;
        _this.activeColIndex = 0;
        /** when true, keyboard navigation will be disabled as the user is e.g. editing an `<input>` inside a cell.  */
        _this.focusInsideCell = false;
        /** When true, all scroll operations noop -- used to prevent scrolling contention when paging in new cells for a focus action. */
        _this.aboutToFocusCell = false;
        _this.gridElement = null;
        _this.bodyElement = null;
        _this.headerElement = null;
        _this.defaultHeaderHeight = 37;
        _this.defaultAverageColumnWidth = 80;
        _this.state = {
            focusLocation: 'outside',
            focusVisible: true,
            measuredColumnHeaderWidths: undefined,
            measuredColumnBodyWidths: undefined,
            userSizedColumnWidths: {},
            columnWidths: undefined,
            averageColumnWidth: 0,
            scrollLeft: 0,
            rows: [],
            gridWidth: 0,
            gridHeight: 0,
            pageIndexes: {
                startColIdx: 0,
                endColIdx: -1,
                startRowIdx: 0,
                endRowIdx: -1
            },
            selectionTimestamp: 0,
        };
        _this.areColumnsEqual = function (nextCols) {
            if (_this.props.cols.length !== nextCols.length) {
                return false;
            }
            for (var idx = 0; idx < nextCols.length; idx++) {
                if (_this.props.cols[idx].name !== nextCols[idx].name) {
                    return false;
                }
            }
            return true;
        };
        _this.isPageIndexesEqualToInitialValue = function () {
            return _this.state.pageIndexes.startColIdx === 0 &&
                _this.state.pageIndexes.endColIdx === -1 &&
                _this.state.pageIndexes.startRowIdx === 0 &&
                _this.state.pageIndexes.endRowIdx === -1;
        };
        _this.fetchForNextProps = function (nextProps) {
            var partialStateUpdate = _this.removeStaleColumnWidths(nextProps);
            if (!_this.areColumnsEqual(nextProps.cols) || _this.isPageIndexesEqualToInitialValue() || _this.props.rowStore !== nextProps.rowStore) {
                partialStateUpdate.columnWidths = _this.updateColumnWidths(partialStateUpdate.measuredColumnBodyWidths || {}, partialStateUpdate.measuredColumnHeaderWidths || {}, nextProps.cols, _this.state.gridWidth);
                partialStateUpdate.pageIndexes = _this.getPageIndexesFromScrollPosition(_this.state.scrollLeft, nextProps.cols);
                _this.fetchUsingProps(nextProps, partialStateUpdate.pageIndexes ? partialStateUpdate.pageIndexes.startRowIdx : _this.state.pageIndexes.startRowIdx, partialStateUpdate.pageIndexes ? partialStateUpdate.pageIndexes.endRowIdx : _this.state.pageIndexes.endRowIdx, partialStateUpdate);
            }
        };
        _this.getGridClientRect = function () {
            if (!_this.gridElement) {
                return { width: 0, height: 0 };
            }
            return _this.gridElement.getBoundingClientRect();
        };
        _this.getBodyClientRect = function () {
            if (!_this.bodyElement) {
                return { width: 0, height: 0 };
            }
            return _this.bodyElement.getBoundingClientRect();
        };
        _this.onResize = function () {
            if (!_this.gridElement) {
                return;
            }
            var rect = _this.getGridClientRect();
            var gridHeight = Math.floor(rect.height);
            var gridWidth = Math.floor(rect.width);
            var columnWidths = _this.updateColumnWidths(_this.state.measuredColumnBodyWidths || {}, _this.state.measuredColumnHeaderWidths || {}, _this.props.cols, gridWidth);
            var pageIndexes = _this.getPageIndexesFromScrollPosition(_this.state.scrollLeft, _this.props.cols);
            var stateUpdate = {
                columnWidths: columnWidths,
                gridHeight: gridHeight,
                gridWidth: gridWidth,
                pageIndexes: pageIndexes
            };
            if (_this.isFetchRequiredForPageIndexes(pageIndexes)) {
                _this.fetchUsingPageIndexes(pageIndexes, stateUpdate);
            }
            else {
                _this.setState(stateUpdate);
            }
        };
        _this.resizeProps = {
            onResize: _this.onResize,
            handleWidth: true,
            handleHeight: true,
            skipOnMount: false,
            refreshMode: 'debounce',
            refreshRate: 0,
        };
        _this.fetchUsingPageIndexes = function (pageIndexes, partialStateUpdate) {
            _this.fetchUsingProps(_this.props, pageIndexes.startRowIdx, pageIndexes.endRowIdx, partialStateUpdate);
        };
        _this.fetchUsingProps = function (props, start, end, partialStateUpdate) {
            start = Math.max(start, 0);
            end = Math.max(end, 0);
            if (props.rowStore) {
                props.rowStore.getItems(start, end, function (rows) {
                    if (!rows || !_this.gridElement) {
                        return;
                    }
                    _this.setState(tslib_1.__assign({ rows: rows }, partialStateUpdate), function () {
                        if (_this.props.updated) {
                            _this.props.updated(rows);
                        }
                        var nextPageIndexes = _this.getPageIndexesFromScrollPosition(_this.state.scrollLeft, props.cols);
                        if (!_this.arePageIndexesEqual(_this.state.pageIndexes, nextPageIndexes)) {
                            var stateUpdate = { pageIndexes: nextPageIndexes };
                            _this.fetchUsingProps(_this.props, nextPageIndexes.startRowIdx, nextPageIndexes.endRowIdx, stateUpdate);
                        }
                    });
                });
            }
            else if (partialStateUpdate && Object.keys(partialStateUpdate).length > 0) {
                _this.setState(partialStateUpdate);
            }
        };
        _this.handleColumnResize = function (column, width) {
            var newColumnWidth = {};
            newColumnWidth[column.name] = width;
            _this.resizing = true;
            _this.setState({
                userSizedColumnWidths: tslib_1.__assign(tslib_1.__assign({}, _this.state.userSizedColumnWidths), newColumnWidth),
                columnWidths: tslib_1.__assign(tslib_1.__assign({}, _this.state.columnWidths), newColumnWidth)
            });
        };
        _this.handleColumnResizeFinish = function () { return _this.resizing = false; };
        _this.handleMeasuredColumnHeaderWidths = function (widths) {
            _this.mergedMeasuredColumnHeaderWidths = tslib_1.__assign(tslib_1.__assign({}, _this.state.measuredColumnHeaderWidths || {}), widths);
            _this.updateStateForMeasuredColumnWidths();
        };
        _this.handleMeasuredColumnBodyWidths = function (widths) {
            _this.mergedMeasuredColumnBodyWidths = tslib_1.__assign(tslib_1.__assign({}, _this.state.measuredColumnBodyWidths || {}), widths);
            _this.updateStateForMeasuredColumnWidths();
        };
        _this.handleRef = function (gridElement) {
            if (gridElement) {
                // React doesn't have a captured mouseEnter event so we bind the native version here.
                // This could be trivially switched to onMouseOverCapture, but that is a breaking change
                // for any consumers watching for `mouseenter` selection events. TFSID 882922
                gridElement.addEventListener('mouseenter', _this.handleMouseSelection, true);
                // React doesn't fire synthetic `blur` events when a focused child element is removed from the DOM
                // So we bind a focusout handler here for listening to bubbling blur events
                // See https://github.com/facebook/react/issues/9142
                gridElement.addEventListener('focusout', _this.handleBlur);
            }
            _this.gridElement = gridElement;
        };
        _this.handleBodyRef = function (body) {
            _this.body = body;
            try {
                _this.bodyElement = ReactDOM.findDOMNode(body);
            }
            catch (e) {
                _this.bodyElement = null;
            }
            _this.updateStateForMeasuredColumnWidths();
        };
        _this.handleHeaderRef = function (header) {
            try {
                _this.headerElement = ReactDOM.findDOMNode(header);
            }
            catch (e) {
                _this.headerElement = null;
            }
        };
        _this.handleBlur = function (e) {
            var relatedTarget = e.relatedTarget;
            var focusLocation = _this.getFocusLocationFromNode(relatedTarget);
            if (focusLocation === 'outside') {
                _this.focusInsideCell = false;
            }
            // Default `focusVisible` to `true` when focus leaves the grid, so that the indicator shows on an initial TAB-into the grid. When
            // a mouse interaction focuses the grid, the mouseDown event precedes the focus event, so by setting `focusVisible` to `false` in that
            // handler, the indicator will never show (i.e. no flicker) for mouse-focus, and will clear if the user switches from keyboard to mouse
            _this.setStateIfNew({ focusLocation: focusLocation, focusVisible: _this.state.focusVisible || focusLocation === 'outside' });
        };
        _this.handleFocus = widgets_1.mergeEventHandlers(function (e) {
            if (e.target === e.currentTarget && _this.state.focusVisible) { // we either tabbed onto the grid element, or clicked it -- ignore the latter
                _this.focusActiveCell(); // redirect focus onto active cell element
                return;
            }
            var targetCell = _this.getCellFromEventTarget(e.target);
            if (!targetCell) {
                return;
            }
            var targetRole = e.target.getAttribute('role');
            _this.focusInsideCell = (targetRole !== 'gridcell' && targetRole !== 'columnheader');
            _this.activeColIndex = targetCell.colIdx;
            _this.activeRowIndex = targetCell.rowIdx;
            _this.setStateIfNew({ focusLocation: targetCell.rowIdx < 0 ? 'header' : 'body' });
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleSelectRowClick = function (rowIdx, e) {
            _this.dispatchSelectionEvent(-1, rowIdx, e);
        };
        _this.handleSelectAllClick = function (e) {
            _this.dispatchSelectionEvent(-1, -1, e);
        };
        _this.handleMouseSelection = function (e) {
            if (_this.state.focusVisible && e.type.indexOf('down') > 0) {
                _this.setState({ focusVisible: false }); // see comments in `handleBlur`
            }
            ;
            if (!_this.props.onSelectionEvent || _this.resizing || (e.type === 'mouseenter' && e.buttons !== 1)) { // TFSID 881510 touch events don't include `buttons`
                return;
            }
            var targetCell = _this.getCellFromEventTarget(e.target);
            if (!targetCell) {
                return;
            }
            _this.dispatchSelectionEvent(targetCell.colIdx, targetCell.rowIdx, e);
        };
        _this.handleKeyDown = widgets_1.mergeEventHandlers(function (e) {
            var _a;
            _this.setStateIfNew({ focusVisible: true });
            // This should be impossible but (at least in Chrome) we sometimes receive a `focusout` event with a missing `relatedTarget` field, even
            // though focus *is* moving to another element. When this happens, DataGrid gets into a bad state, so this is a safety check to get back
            // into a good state. TFSID 1220408
            if (_this.state.focusLocation === 'outside') {
                // it's rude to assume implementation details, but it's also rude of the browser to not give us `relatedTarget`
                _this.handleFocus(e);
                return;
            }
            var targetRole = e.target.getAttribute('role');
            if (!_this.props.__UNSTABLE__headerAccessibility && _this.state.focusLocation === 'header') {
                // allow focus to escape a header column if it contains something focusable
                if (e.key === 'ArrowDown') {
                    _this.focusInsideCell = false;
                }
                else {
                    return;
                }
            }
            else if ((targetRole !== 'gridcell' && (!_this.props.__UNSTABLE__headerAccessibility || targetRole !== 'columnheader')) && _this.focusInsideCell) {
                if (e.key === 'Escape') {
                    _this.focusInsideCell = false;
                    _this.focusActiveCell();
                    e.stopPropagation();
                    e.preventDefault();
                }
                return; // ignore all other key events that occur inside of cells, e.g. in an <input>
            }
            var colIdx = _this.activeColIndex;
            var rowIdx = _this.activeRowIndex;
            switch (e.key) {
                case 'Enter':
                    _this.focusInsideCell = true;
                    _this.focusActiveCell();
                    e.stopPropagation();
                    e.preventDefault();
                    return;
                case 'a':
                case 'A':
                case ' ':
                case 'Escape':
                    if (_this.props.onSelectionEvent) {
                        _this.dispatchSelectionEvent(colIdx, rowIdx, e);
                        e.stopPropagation();
                        e.preventDefault();
                    }
                    return;
                case 'ArrowUp':
                    rowIdx--;
                    break;
                case 'ArrowDown':
                    rowIdx++;
                    break;
                case 'ArrowLeft':
                    colIdx--;
                    break;
                case 'ArrowRight':
                    colIdx++;
                    break;
                case 'PageUp':
                    rowIdx -= Math.floor(_this.rowsWithinViewPort());
                    rowIdx = Math.max(0, rowIdx); // don't pageup into column headers
                    break;
                case 'PageDown':
                    rowIdx += Math.floor(_this.rowsWithinViewPort());
                    break;
                case 'Home':
                    if (e.ctrlKey) {
                        rowIdx = 0;
                    }
                    colIdx = 0;
                    break;
                case 'End':
                    if (e.ctrlKey) {
                        rowIdx = _this.state.rows.length - 1;
                    }
                    colIdx = _this.props.cols.length - 1;
                    break;
                default:
                    return;
            }
            _a = _this.clampCellIndexes(rowIdx, colIdx), rowIdx = _a[0], colIdx = _a[1];
            if (colIdx !== _this.activeColIndex || rowIdx !== _this.activeRowIndex) {
                _this.dispatchSelectionEvent(colIdx, rowIdx, e);
                _this.focusCell(rowIdx, colIdx);
                e.stopPropagation();
            }
            e.preventDefault();
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.onBodyScroll = function (left, top) {
            if (_this.aboutToFocusCell) {
                return;
            }
            _this.scrollTop = top;
            var partialStateUpdate = {};
            if (left !== _this.state.scrollLeft) {
                partialStateUpdate.scrollLeft = left;
            }
            var nextPageIndexes = _this.getPageIndexesFromScrollPosition(left, _this.props.cols);
            if (!_this.arePageIndexesEqual(_this.state.pageIndexes, nextPageIndexes)) {
                partialStateUpdate.pageIndexes = nextPageIndexes;
            }
            if (_this.isFetchRequiredForPageIndexes(nextPageIndexes)) {
                _this.fetchUsingPageIndexes(nextPageIndexes, partialStateUpdate);
            }
            else if (Object.keys(partialStateUpdate).length > 0) {
                _this.setState(partialStateUpdate);
            }
        };
        return _this;
    }
    DataGrid.prototype.setStateIfNew = function (stateUpdate, callback) {
        for (var _i = 0, _a = Object.keys(stateUpdate); _i < _a.length; _i++) {
            var key = _a[_i];
            if (stateUpdate[key] !== this.state[key]) {
                this.setState(stateUpdate, callback);
                return;
            }
        }
    };
    DataGrid.prototype.componentDidMount = function () {
        var rect = this.getGridClientRect();
        var gridWidth = Math.floor(rect.width);
        var gridHeight = Math.floor(rect.height);
        var pageIndexes = this.getPageIndexesFromScrollPosition(this.state.scrollLeft, this.props.cols);
        var stateUpdate = {
            gridWidth: gridWidth,
            gridHeight: gridHeight,
            columnWidths: this.updateColumnWidths({}, {}, this.props.cols, gridWidth),
            pageIndexes: pageIndexes
        };
        this.fetchUsingPageIndexes(pageIndexes, stateUpdate);
    };
    DataGrid.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        if (this.props.rowStore !== nextProps.rowStore) {
            // A new row store is created when order or filter params have changed, so
            // scroll to the top (unless user explicitly sets rememberLastScrollPos in the config).
            if (!!this.body) {
                this.body.onRowStoreChanged();
            }
        }
        this.fetchForNextProps(nextProps);
    };
    DataGrid.prototype.componentDidUpdate = function () {
        this.maybeUpdateStateForMeasuredColumnWidths();
    };
    // Sometimes we don't know that we need to update the column widths until after the data-grid has rendered.
    // This method checks if that is the case, and if so, updates the column widths.
    DataGrid.prototype.maybeUpdateStateForMeasuredColumnWidths = function () {
        var _this = this;
        var isColumnWidthChanged = false;
        var gridWidth = this.getGridWidth();
        var newColumnWidths = this.getCalculatedColumnWidths(gridWidth);
        var scrollbarIsNeeded = this.computeIsScrollbarNeeded();
        Object.keys(newColumnWidths || {}).some(function (key) {
            if (newColumnWidths[key] !== (_this.state.columnWidths && _this.state.columnWidths[key])) {
                isColumnWidthChanged = true;
            }
            return isColumnWidthChanged;
        });
        // TFSID 1082053: If a vertical scrollbar is needed, we'll need to recalculate the column widths to account for that space
        if (scrollbarIsNeeded !== this.scrollbarWasNeeded) {
            isColumnWidthChanged = true;
            this.scrollbarWasNeeded = scrollbarIsNeeded;
        }
        if (isColumnWidthChanged) {
            this.updateStateForMeasuredColumnWidths();
        }
    };
    /** @returns [rowIdx, colIdx] */
    DataGrid.prototype.clampCellIndexes = function (rowIdx, colIdx) {
        var minRowIdx = this.props.__UNSTABLE__headerAccessibility ? -1 : 0;
        return [
            /* rowIdx */ Utils_1.clamp(rowIdx, minRowIdx, Math.max(minRowIdx, this.state.rows.length - 1)),
            /* colIdx */ Utils_1.clamp(colIdx, 0, Math.max(0, this.props.cols.length - 1)),
        ];
    };
    DataGrid.prototype.focusCell = function (rowIdx, colIdx) {
        var _a;
        var _this = this;
        if (colIdx === void 0) { colIdx = this.activeColIndex; }
        if (this.state.rows.length === 0 || this.props.cols.length === 0) {
            return;
        }
        _a = this.clampCellIndexes(rowIdx, colIdx), rowIdx = _a[0], colIdx = _a[1];
        var cellElem = this.findCellElement({ colIdx: colIdx, rowIdx: rowIdx });
        if (!cellElem) { // attempting to focus a cell which (for perf reasons) is not currently rendered in the DOM
            this.aboutToFocusCell = true; // disable body scroll position fixing in order to page in the cell-to-focus
            this.setState({
                pageIndexes: Paging_1.Paging.getSectionedPageIndexesForCell(colIdx, rowIdx, this.state.rows.length || Math.ceil(this.rowsWithinViewPort()), this.props.cols, this.state.columnWidths || {}, this.minimumColumnWidth, this.getBodyClientRect(), this.rowOuterHeight, this.colsPerChunk, this.sectionRows)
            }, function () {
                _this.aboutToFocusCell = false;
                // call this method again now that the cell-to-focus is rendered
                _this.focusCell(rowIdx, colIdx);
            });
            return;
        }
        if (this.focusInsideCell) {
            var focusableChild = cellElem.querySelector('input:enabled, textarea:enabled, select:enabled, button:enabled, a[href], [tabindex]');
            if (focusableChild) {
                focusableChild.focus();
                return;
            }
            else {
                this.focusInsideCell = false;
            }
        }
        cellElem.focus();
    };
    DataGrid.prototype.focusCellByKey = function (rowKey, colKey) {
        if (this.state.rows.length === 0 || this.props.cols.length === 0) {
            return;
        }
        var rowIdx = this.state.rows.findIndex(function (row) { return row.key === rowKey; });
        var colIdx = colKey ? this.props.cols.findIndex(function (col) { return col.dataKey === colKey; }) : this.activeColIndex;
        if (rowIdx === -1 || colIdx === -1) {
            return;
        }
        this.focusCell(rowIdx, colIdx);
    };
    DataGrid.prototype.focusActiveCell = function () {
        this.focusCell(this.activeRowIndex, this.activeColIndex);
    };
    /** Returns the column and row index for the currently-active cell */
    DataGrid.prototype.getActiveCell = function () {
        var _a;
        return {
            rowIdx: this.activeRowIndex,
            colIdx: this.activeColIndex,
            colKey: this.props.cols[this.activeColIndex].dataKey,
            rowKey: (_a = this.state.rows[this.activeRowIndex]) === null || _a === void 0 ? void 0 : _a.key,
        };
    };
    DataGrid.prototype.findCellElement = function (cell) {
        var _a, _b, _c, _d;
        if (cell.rowIdx < 0) {
            return _b = (_a = this.headerElement) === null || _a === void 0 ? void 0 : _a.querySelector("[role=\"columnheader\"][aria-colindex=\"" + (cell.colIdx + 1) + "\"]"), (_b !== null && _b !== void 0 ? _b : null);
        }
        return _d = (_c = this.bodyElement) === null || _c === void 0 ? void 0 : _c.querySelector("[aria-rowindex=\"" + (cell.rowIdx + (this.props.showHeaders ? 2 : 1)) + "\"][aria-colindex=\"" + (cell.colIdx + 1) + "\"][role=\"gridcell\"]"), (_d !== null && _d !== void 0 ? _d : null);
    };
    DataGrid.prototype.removeStaleColumnWidths = function (nextProps) {
        var _this = this;
        var trimmedColumnWidths = {};
        var trimmedMeasuredColumnHeaderWidths = {};
        var trimmedMeasuredColumnBodyWidths = {};
        nextProps.cols.forEach(function (col) {
            var columnKey = col.name;
            if (_this.state.columnWidths && _this.state.columnWidths[columnKey]) {
                trimmedColumnWidths[columnKey] = _this.state.columnWidths[columnKey];
            }
            if (_this.state.measuredColumnHeaderWidths && _this.state.measuredColumnHeaderWidths[columnKey]) {
                trimmedMeasuredColumnHeaderWidths[columnKey] = _this.state.measuredColumnHeaderWidths[columnKey];
            }
            if (_this.state.measuredColumnBodyWidths && _this.state.measuredColumnBodyWidths[columnKey]) {
                trimmedMeasuredColumnBodyWidths[columnKey] = _this.state.measuredColumnBodyWidths[columnKey];
            }
        });
        return {
            columnWidths: trimmedColumnWidths,
            measuredColumnHeaderWidths: trimmedMeasuredColumnHeaderWidths,
            measuredColumnBodyWidths: trimmedMeasuredColumnBodyWidths
        };
    };
    DataGrid.prototype.arePageIndexesEqual = function (currentPageIndexes, nextPageIndexes) {
        return currentPageIndexes.startColIdx === nextPageIndexes.startColIdx &&
            currentPageIndexes.endColIdx === nextPageIndexes.endColIdx &&
            currentPageIndexes.startRowIdx === nextPageIndexes.startRowIdx &&
            currentPageIndexes.endRowIdx === nextPageIndexes.endRowIdx;
    };
    DataGrid.prototype.isFetchRequiredForPageIndexes = function (nextPageIndexes) {
        return nextPageIndexes.startRowIdx < this.state.pageIndexes.startRowIdx || nextPageIndexes.endRowIdx > this.state.pageIndexes.endRowIdx;
    };
    DataGrid.prototype.getCalculatedColumnWidths = function (gridWidth) {
        return this.updateColumnWidths(this.mergedMeasuredColumnBodyWidths || {}, this.mergedMeasuredColumnHeaderWidths || {}, this.props.cols, gridWidth);
    };
    DataGrid.prototype.getGridWidth = function () {
        return this.state.gridWidth || Math.floor(this.getGridClientRect().width);
    };
    /**
     * This method uses the measured column widths from the body and header components to determine each column's width.
     * Typically, a column width will be the maximum width of the two measurements.
     */
    DataGrid.prototype.updateStateForMeasuredColumnWidths = function () {
        var haveColumnBodyWidthMeasurements = (this.state.rows.length === 0 || this.mergedMeasuredColumnBodyWidths);
        var haveColumnHeaderWidthMeasurements = (!this.props.showHeaders || this.mergedMeasuredColumnHeaderWidths);
        if (haveColumnBodyWidthMeasurements && haveColumnHeaderWidthMeasurements) {
            var gridWidth = this.getGridWidth();
            var newColumnWidths = this.getCalculatedColumnWidths(gridWidth);
            var newAverageColumnWidth = this.calculateAverageColumnWidth(newColumnWidths);
            var columnWidthsStateUpdate = {
                gridWidth: gridWidth,
                columnWidths: newColumnWidths,
                averageColumnWidth: newAverageColumnWidth,
                measuredColumnBodyWidths: tslib_1.__assign({}, this.mergedMeasuredColumnBodyWidths),
                measuredColumnHeaderWidths: tslib_1.__assign({}, this.mergedMeasuredColumnHeaderWidths)
            };
            this.setState(columnWidthsStateUpdate);
        }
    };
    DataGrid.prototype.computeIsScrollbarNeeded = function () {
        if (!this.bodyElement || this.state.rows.length === 0) {
            return false;
        }
        var totalRowHeight = this.rowOuterHeight * this.state.rows.length;
        var bodyHeight = this.getBodyClientRect().height;
        return totalRowHeight > bodyHeight;
    };
    DataGrid.prototype.getScrollbarWidth = function () {
        return this.computeIsScrollbarNeeded() ? Utils_1.measureScrollbarWidth(this.bodyElement) : 0;
    };
    DataGrid.prototype.updateColumnWidths = function (measuredColumnBodyWidths, measuredColumnHeaderWidths, cols, gridWidth) {
        var _this = this;
        var maxInitialColumnWidth = this.config.maxInitialColumnWidth || ConfigDefaults.maxInitialColumnWidth;
        var columnWidths = {};
        var maxMeasuredColumnWidths = {};
        var totalWidth = 0;
        cols.forEach(function (col) {
            var currentColumnBodyWidth;
            var currentColumnHeaderWidth;
            var columnKey = col.name;
            var maxColumnWidth = 0;
            if (_this.state.userSizedColumnWidths[columnKey]) {
                columnWidths[columnKey] = _this.state.userSizedColumnWidths[columnKey];
                totalWidth += columnWidths[columnKey];
            }
            else {
                if (!col.preferredWidth) {
                    currentColumnBodyWidth = measuredColumnBodyWidths[columnKey];
                    currentColumnHeaderWidth = measuredColumnHeaderWidths[columnKey];
                    maxColumnWidth = Math.max(currentColumnBodyWidth || 0, currentColumnHeaderWidth || 0);
                }
                maxMeasuredColumnWidths[columnKey] = col.preferredWidth || maxColumnWidth;
                if (col.preferredWidth || currentColumnBodyWidth || currentColumnHeaderWidth) {
                    var currentMaxInitialColumnWidth = (maxInitialColumnWidth < 0) ? maxMeasuredColumnWidths[columnKey] : maxInitialColumnWidth;
                    columnWidths[columnKey] = Math.min(maxMeasuredColumnWidths[columnKey], currentMaxInitialColumnWidth);
                    totalWidth += columnWidths[columnKey];
                }
            }
        });
        if (this.props.showRowHeaders) {
            totalWidth += RowHeaderCell_1.RowHeaderCell.Width;
        }
        // Allocating unused width in left-to-right column order, up to each columns's max width
        var extraAvailable = gridWidth - this.getScrollbarWidth() - (this.props.leftMargin || 0) - totalWidth;
        var growToFitCols = 0;
        for (var colIdx = 0; colIdx < cols.length && extraAvailable > 0; colIdx++) {
            var col = cols[colIdx];
            var maxColWidth = col.maxColumnWidth || maxMeasuredColumnWidths[col.name];
            var extraRequested = Math.min(maxColWidth, maxMeasuredColumnWidths[col.name]) - columnWidths[col.name];
            if (extraRequested > 0 && columnWidths[col.name] !== undefined) {
                var extraAllocated = Math.min(extraRequested, extraAvailable);
                columnWidths[col.name] += extraAllocated;
                extraAvailable -= extraAllocated;
            }
            if (col.growToFit && !this.state.userSizedColumnWidths[col.name]) {
                growToFitCols += 1;
            }
        }
        // Allocate remaining width evenly to any growToFit columns
        if (extraAvailable > 0 && growToFitCols > 0) {
            var extraWidth_1 = extraAvailable / growToFitCols;
            cols.forEach(function (col) {
                if (col.growToFit && !_this.state.userSizedColumnWidths[col.name] && columnWidths[col.name] !== undefined) {
                    columnWidths[col.name] += extraWidth_1;
                }
            });
        }
        return columnWidths;
    };
    DataGrid.prototype.getFocusLocationFromNode = function (node) {
        var _a, _b;
        if ((_a = this.bodyElement) === null || _a === void 0 ? void 0 : _a.contains(node)) {
            return 'body';
        }
        else if ((_b = this.headerElement) === null || _b === void 0 ? void 0 : _b.contains(node)) {
            return 'header';
        }
        else {
            return 'outside';
        }
    };
    DataGrid.prototype.dispatchSelectionEvent = function (colIdx, rowIdx, e) {
        if (!this.props.onSelectionEvent) {
            return;
        }
        var colKey = colIdx >= 0 && this.props.cols[colIdx] ? this.props.cols[colIdx].dataKey : undefined;
        var rowKey = rowIdx >= 0 && this.state.rows[rowIdx] ? this.state.rows[rowIdx].key : undefined;
        var _a = e, altKey = _a.altKey, shiftKey = _a.shiftKey, key = _a.key, button = _a.button, buttons = _a.buttons, type = _a.type;
        var ctrlKey = Utils_1.isCtrl(e);
        var selectionEvent = {
            cell: { colIdx: colIdx, rowIdx: rowIdx, colKey: colKey, rowKey: rowKey }, altKey: altKey, ctrlKey: ctrlKey, shiftKey: shiftKey, key: key, button: button, buttons: buttons, type: type
        };
        if (this.props.onSelectionEvent(selectionEvent)) {
            this.setState({ selectionTimestamp: this.state.selectionTimestamp + 1 });
        }
    };
    DataGrid.prototype.render = function () {
        // tslint:disable-next-line:deprecation max-line-length
        var _a = this.props, __UNSTABLE__headerAccessibility = _a.__UNSTABLE__headerAccessibility, cols = _a.cols, config = _a.config, emptyChild = _a.emptyChild, headerHeightProp = _a.headerHeight, leftMargin = _a.leftMargin, onCellClick = _a.onCellClick, onCellContextMenu = _a.onCellContextMenu, onCellDoubleClick = _a.onCellDoubleClick, onHeaderClick = _a.onHeaderClick, onHeaderContextMenu = _a.onHeaderContextMenu, onHeaderDoubleClick = _a.onHeaderDoubleClick, onSelectionEvent = _a.onSelectionEvent, rowStore = _a.rowStore, scrollComponent = _a.scrollComponent, scrollableHandler = _a.scrollableHandler, scrollingRootId = _a.scrollingRootId, selectionModel = _a.selectionModel, showHeaders = _a.showHeaders, showHorizontalGridLines = _a.showHorizontalGridLines, showRowBanding = _a.showRowBanding, showRowHeaders = _a.showRowHeaders, showVerticalGridLines = _a.showVerticalGridLines, sortAscending = _a.sortAscending, sortKey = _a.sortKey, style = _a.style, updated = _a.updated, htmlProps = tslib_1.__rest(_a, ["__UNSTABLE__headerAccessibility", "cols", "config", "emptyChild", "headerHeight", "leftMargin", "onCellClick", "onCellContextMenu", "onCellDoubleClick", "onHeaderClick", "onHeaderContextMenu", "onHeaderDoubleClick", "onSelectionEvent", "rowStore", "scrollComponent", "scrollableHandler", "scrollingRootId", "selectionModel", "showHeaders", "showHorizontalGridLines", "showRowBanding", "showRowHeaders", "showVerticalGridLines", "sortAscending", "sortKey", "style", "updated"]);
        this.config = config || {}; // bad practice right here
        this.colsPerChunk = this.config.colsPerChunk || ConfigDefaults.colsPerChunk;
        this.sectionRows = this.config.sectionRows || ConfigDefaults.sectionRows;
        this.rowOuterHeight = this.config.rowOuterHeight || ConfigDefaults.rowOuterHeight;
        this.minimumColumnWidth = this.config.minimumColumnWidth || ConfigDefaults.minimumColumnWidth;
        var rows = this.state.rows;
        var headerHeight = showHeaders ? (headerHeightProp || this.defaultHeaderHeight) : 0;
        var columnWidths = this.state.columnWidths;
        var scrollLeft = this.state.scrollLeft || 0;
        var columnWidthInfo = this.getColumnWidthInfo();
        var pageIndexes = this.state.pageIndexes;
        var colEndIdx = Math.min(pageIndexes.endColIdx, cols.length - 1);
        var children = [];
        if (Array.isArray(rows)) {
            if (colEndIdx >= pageIndexes.startColIdx && showHeaders) {
                children.push(React.createElement(HeaderRow_1.HeaderRow, tslib_1.__assign({}, {
                    key: 'header',
                    ref: this.handleHeaderRef,
                    headerHeight: headerHeight,
                    rowCount: rows.length,
                    cols: cols,
                    colStartIdx: pageIndexes.startColIdx,
                    colEndIdx: colEndIdx,
                    minColWidth: this.minimumColumnWidth,
                    columnWidths: columnWidths || this.getProvisionalColumnWidths(),
                    leftMargin: columnWidthInfo.leftSpacer + (leftMargin || 0),
                    sortKey: sortKey,
                    sortAscending: sortAscending !== false,
                    onHeaderClick: onHeaderClick,
                    showVerticalGridLines: showVerticalGridLines,
                    onRowHeaderCellClick: showRowHeaders ? this.handleSelectAllClick : undefined,
                    onHeaderDoubleClick: onHeaderDoubleClick,
                    onHeaderContextMenu: onHeaderContextMenu,
                    measuredColumnWidths: this.state.measuredColumnHeaderWidths || {},
                    measureColumnWidths: this.handleMeasuredColumnHeaderWidths,
                    scrollLeft: scrollLeft,
                    onColumnResize: this.handleColumnResize,
                    onColumnResizeFinish: this.handleColumnResizeFinish,
                    selectionModel: selectionModel,
                    selectionTimestamp: this.state.selectionTimestamp,
                    keyboardAccessible: __UNSTABLE__headerAccessibility,
                })));
            }
            children.push(React.createElement(Body_1.Body, tslib_1.__assign({}, {
                key: 'body',
                ref: this.handleBodyRef,
                headerHeight: headerHeight,
                showRowBanding: showRowBanding,
                showHorizontalGridLines: showHorizontalGridLines,
                showVerticalGridLines: showVerticalGridLines,
                cols: cols,
                columnWidths: columnWidths,
                leftMargin: leftMargin,
                leftSpacer: columnWidthInfo.leftSpacer,
                measuredColumnWidths: this.state.measuredColumnBodyWidths || {},
                measureColumnWidths: this.handleMeasuredColumnBodyWidths,
                onBodyScroll: this.onBodyScroll,
                rows: rows,
                emptyChild: emptyChild,
                selectionModel: selectionModel,
                selectionTimestamp: this.state.selectionTimestamp,
                onCellClick: onCellClick,
                onCellDoubleClick: onCellDoubleClick,
                onCellContextMenu: onCellContextMenu,
                onRowHeaderCellClick: showRowHeaders ? this.handleSelectRowClick : undefined,
                scrollComponent: scrollComponent || DataGrid.defaultScroller,
                scrollingRootId: scrollingRootId,
                onIScrollableRefUpdate: scrollableHandler,
                rowOuterHeight: this.config.rowOuterHeight || ConfigDefaults.rowOuterHeight,
                colsPerChunk: this.config.colsPerChunk || ConfigDefaults.colsPerChunk,
                sectionRows: this.config.sectionRows || ConfigDefaults.sectionRows,
                rememberLastScrollPos: this.config.rememberLastScrollPos || ConfigDefaults.rememberLastScrollPos,
                pageIndexes: pageIndexes,
                totalColumnWidth: columnWidthInfo.totalColumnWidth
            })));
        }
        var rowCount = this.state.rows.length + (showHeaders ? 1 : 0);
        return (React.createElement("div", tslib_1.__assign({ role: "grid", "aria-colcount": cols.length, "aria-rowcount": rowCount }, htmlProps, { className: TypeStyle.classes(htmlProps.className, 'tb-react-data-grid', DataGridColors_1.FocusIndicatorStyleName, { 'focus-visible': this.state.focusVisible }), onMouseDown: this.handleMouseSelection, onKeyDown: this.handleKeyDown, onFocus: this.handleFocus, ref: this.handleRef, style: tslib_1.__assign({ 
                // DefaultScrollComponent is absolutely positioned so the grid element needs to be the relative parent
                position: 'relative', overflow: 'hidden', height: '100%', outline: 'none' }, style), tabIndex: this.state.focusLocation === 'outside' ? 0 : undefined }),
            React.createElement(exports.ColumnHeadersContext.Provider, { value: !!showHeaders }, children),
            React.createElement(ReactResizeDetector.default, tslib_1.__assign({}, this.resizeProps))));
    };
    DataGrid.prototype.getCellFromEventTarget = function (target) {
        var targetCell = target;
        // IE11 doesn't have Element.closest so we walk the tree instead
        while (targetCell) {
            var role = targetCell.getAttribute('role');
            if (role === 'gridcell' || role === 'columnheader') {
                break;
            }
            else if (role === 'separator') {
                if (targetCell.parentElement && targetCell.parentElement.getAttribute('role') === 'columnheader') {
                    // don't interfere with the mouse drag handler for column resizing
                    return null;
                }
            }
            else if (targetCell === this.gridElement) {
                // don't go outside our tree
                return null;
            }
            targetCell = targetCell.parentElement;
        }
        if (!targetCell) {
            return null;
        }
        return {
            // aria-rowindex and aria-colindex count from 1, not 0
            rowIdx: parseInt(targetCell.getAttribute('aria-rowindex') || '', 10) - (this.props.showHeaders ? 2 : 1),
            colIdx: parseInt(targetCell.getAttribute('aria-colindex') || '', 10) - 1
        };
    };
    DataGrid.prototype.rowsWithinViewPort = function () {
        var bodyRect = this.getBodyClientRect();
        return bodyRect.height / this.rowOuterHeight;
    };
    /**
     * This method uses the scroll position and column widths to determine the range of columns and rows that will
     * be rendered to the Virtual DOM. The range of columns and rows will overlap the viewport on all sides. The
     * first and last column/row indexes are then normalized against the first and last column/row indexes for the
     * corresponding bandings.
     */
    DataGrid.prototype.getPageIndexesFromScrollPosition = function (scrollLeft, cols) {
        return Paging_1.Paging.getSectionedPageIndexesForScrollPosition(scrollLeft, this.scrollTop, this.state.rows.length || Math.ceil(this.rowsWithinViewPort()), cols, this.state.columnWidths || {}, this.getStateAverageColumnWidth(), this.getBodyClientRect(), this.rowOuterHeight, this.colsPerChunk, this.sectionRows);
    };
    /**
     * getProvisionalColumnWidths is used when we have no data rows, so can't measure the width of the data columns.
     * We just choose reasonable widths for the headers.
     */
    DataGrid.prototype.getProvisionalColumnWidths = function () {
        var _this = this;
        // Only compute provisional column widths if the header widths have already
        // been measured
        if (!this.state.measuredColumnHeaderWidths) {
            return null;
        }
        // Find the total width of the measured headers
        var totalWidth = 0;
        var numberOfResizableColumns = 0;
        var cols = this.props.cols || [];
        cols.forEach(function (col) {
            if (col.preferredWidth) {
                totalWidth += col.preferredWidth;
            }
            else {
                if (_this.state.measuredColumnHeaderWidths) {
                    totalWidth += _this.state.measuredColumnHeaderWidths[col.name];
                }
            }
            if (col.resizable !== false) {
                // default (undefined) is resizable
                numberOfResizableColumns++;
            }
        });
        if (this.props.showRowHeaders) {
            totalWidth += RowHeaderCell_1.RowHeaderCell.Width;
        }
        // Allocate any extra space evenly between the resizable headers
        var extraAvailable = this.state.gridWidth - this.getScrollbarWidth() - totalWidth;
        var provisionalColumnWidths = {};
        if (extraAvailable > 0 && numberOfResizableColumns > 0) {
            var extraPerHeader_1 = extraAvailable / numberOfResizableColumns;
            cols.forEach(function (col) {
                if (col.resizable !== false) {
                    // default (undefined) is resizable
                    var width = 0;
                    if (col.preferredWidth) {
                        width = col.preferredWidth;
                    }
                    else {
                        if (_this.state.measuredColumnHeaderWidths) {
                            width = _this.state.measuredColumnHeaderWidths[col.name];
                        }
                    }
                    provisionalColumnWidths[col.name] = width + extraPerHeader_1;
                }
            });
        }
        return provisionalColumnWidths;
    };
    DataGrid.prototype.getColumnWidthInfo = function () {
        var columnWidthInfo = {
            leftSpacer: 0,
            totalColumnWidth: 0
        };
        var columnWidths = this.state.columnWidths || {};
        if (Object.keys(columnWidths).length === 0) {
            return columnWidthInfo;
        }
        var defaultColumnWidth = this.getStateAverageColumnWidth();
        var columnStartIndex = this.state.pageIndexes.startColIdx;
        var totalColumnWidth = this.props.showRowHeaders ? RowHeaderCell_1.RowHeaderCell.Width : 0;
        for (var colIdx = 0; colIdx < this.props.cols.length; colIdx++) {
            totalColumnWidth += columnWidths[this.props.cols[colIdx].name] || defaultColumnWidth;
            if (columnStartIndex !== 0 && (colIdx + 1 === columnStartIndex)) {
                columnWidthInfo.leftSpacer = totalColumnWidth;
            }
        }
        columnWidthInfo.totalColumnWidth = totalColumnWidth;
        return columnWidthInfo;
    };
    DataGrid.prototype.getStateAverageColumnWidth = function () {
        return Math.max(this.state.averageColumnWidth || 0, this.defaultAverageColumnWidth);
    };
    DataGrid.prototype.calculateAverageColumnWidth = function (columnWidths) {
        var colCount = Object.keys(columnWidths).length;
        if (colCount === 0) {
            return this.defaultAverageColumnWidth;
        }
        var sum = Object.keys(columnWidths).map(function (key) { return columnWidths[key]; }).reduce(function (acc, val) {
            return acc + val;
        });
        var averageColumnWidth = Math.ceil(sum / colCount);
        return Math.max(averageColumnWidth, this.defaultAverageColumnWidth);
    };
    DataGrid.defaultScroller = DefaultScrollComponent_1.DefaultScrollComponent;
    DataGrid.displayName = 'DataGrid';
    DataGrid.defaultProps = {
        cols: [],
        showHeaders: true,
        showRowBanding: true,
    };
    return DataGrid;
}(React.Component));
exports.DataGrid = DataGrid;
//# sourceMappingURL=DataGrid.js.map